import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowUpDown, Calendar, ChevronRight } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import InfiniteScroll from "react-infinite-scroll-component";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { NewsSkeleton } from "./NewsSkeleton";
import { format } from "date-fns";
import { useNews } from "../../hooks/useNews";
import {
  trackNewsPageViewed,
  trackNewsCardClick,
} from "@/modules/hub/features/News/analytics/newsAnalytics";

export const News: React.FC = () => {
  const hasTrackedPageView = useRef(false);

  useEffect(() => {
    if (!hasTrackedPageView.current) {
      trackNewsPageViewed();
      hasTrackedPageView.current = true;
    }
  }, []);

  const {
    resources,
    hasMore,
    totalCount,
    loading,
    isInitialLoading,
    fetchMore,
    sortOption,
    setSortOption,
  } = useNews();

  const sortOptions = [
    { value: "newest", label: "Newest First" },
    { value: "oldest", label: "Oldest First" },
    { value: "mostViewed", label: "Most Viewed" },
    { value: "alphabetical", label: "Title A-Z" },
  ];

  if (isInitialLoading) {
    return <NewsSkeleton />;
  }

  return (
    <MainPageWrapper
      subtitle="Latest updates and insights from across the trades"
      title="News"
    >
      <div className="space-y-6">
        {/* Sort Controls */}
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {totalCount > 0 && `${totalCount} news articles found`}
          </div>
          <div className="flex items-center gap-2">
            <ArrowUpDown className="h-4 w-4 text-gray-500" />
            <Select
              onValueChange={(value) => setSortOption(value as any)}
              value={sortOption}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Sort by..." />
              </SelectTrigger>
              <SelectContent>
                {sortOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* News Grid with Infinite Scroll */}
        <InfiniteScroll
          dataLength={resources.length}
          endMessage={
            <div className="text-center py-4">
              <p className="text-gray-500 dark:text-gray-400">
                No more news to load
              </p>
            </div>
          }
          hasMore={hasMore}
          loader={
            <div className="text-center py-4">
              <p className="text-gray-500 dark:text-gray-400">
                Loading more...
              </p>
            </div>
          }
          next={fetchMore}
          style={{ overflow: "visible" }}
        >
          {resources.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No news articles found.</p>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {resources.map((article) => (
                <Link
                  className="block hover:no-underline"
                  key={article.id}
                  onClick={() =>
                    trackNewsCardClick({
                      resourceType: "News",
                      resourceUuid: article.uuid,
                    })
                  }
                  to={`/hub/resources/${article.slug}`}
                >
                  <Card className="h-full transition-shadow hover:shadow-lg">
                    {/* Featured Image */}
                    {article.thumbnailUrl && (
                      <div className="aspect-video w-full overflow-hidden rounded-t-lg">
                        <img
                          alt={article.title}
                          className="h-full w-full object-cover"
                          src={article.thumbnailUrl}
                        />
                      </div>
                    )}

                    <CardContent className="p-6">
                      {/* Date */}
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mb-2">
                        <Calendar className="h-3 w-3" />
                        {article.createdOrPublishStartDate && (
                          <span>
                            {format(
                              new Date(article.createdOrPublishStartDate),
                              "MMM d, yyyy",
                            )}
                          </span>
                        )}
                      </div>

                      {/* Title */}
                      <h3 className="font-semibold text-lg mb-2 line-clamp-2">
                        {article.title}
                      </h3>

                      {/* Description */}
                      {article.searchDescription && (
                        <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
                          {article.searchDescription}
                        </p>
                      )}

                      {/* Read More */}
                      <div className="flex items-center gap-1 text-sm font-medium text-primary mt-auto">
                        Read more
                        <ChevronRight className="h-4 w-4" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </InfiniteScroll>
      </div>
    </MainPageWrapper>
  );
};
